import { useLayoutEffect, useRef } from 'react';
import Link from '@/components/base/TransitionLink';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Img from '@/components/base/Img';
import { projects } from '@/mocks/site';

gsap.registerPlugin(ScrollTrigger);

type Project = (typeof projects)[number];

interface Props {
  p: Project;
  prev: Project;
  next: Project;
}

// Horizontal mode only applies on wide screens without reduced-motion; otherwise chapters stack vertically.
const HZ_QUERY = '(min-width: 1024px) and (prefers-reduced-motion: no-preference)';
const H = 'lg:motion-safe:h-full';
const chapterBase = 'shrink-0 w-full lg:motion-safe:h-full py-12 lg:motion-safe:py-10 border-b lg:motion-safe:border-b-0 border-background-300/60';
const label = 'text-[11px] uppercase tracking-[0.2em] text-foreground-600';

export default function HorizontalDetail({ p, prev, next }: Props) {
  const viewport = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const facts = [
    { k: 'Surface', v: p.surface },
    { k: 'Finish', v: p.finish },
    { k: 'Scope', v: p.scope },
  ];
  const [g0, g1, g2] = p.gallery;

  useLayoutEffect(() => {
    let alive = true;
    const mm = gsap.matchMedia();
    mm.add(HZ_QUERY, () => {
      const vp = viewport.current;
      const tr = track.current;
      if (!vp || !tr) return undefined;
      const distance = () => Math.max(0, tr.scrollWidth - vp.clientWidth);
      gsap.to(tr, {
        x: () => -distance(),
        ease: 'none',
        scrollTrigger: {
          trigger: vp,
          start: 'top top',
          end: () => `+=${distance()}`,
          pin: true,
          scrub: 0.9,
          invalidateOnRefresh: true,
        },
      });
      return undefined;
    });
    // Re-measure once layout-critical fonts are ready (guarded against unmount).
    document.fonts?.ready.then(() => {
      if (alive) ScrollTrigger.refresh();
    });
    return () => {
      alive = false;
      mm.revert(); // kills trigger, removes pin spacer, clears inline transforms
    };
  }, []);

  return (
    <div ref={viewport} className="bg-background-50 overflow-hidden lg:motion-safe:h-screen lg:motion-safe:pt-16">
      <div
        ref={track}
        className="flex flex-col lg:motion-safe:flex-row lg:motion-safe:h-full lg:motion-safe:w-max lg:motion-safe:gap-[6vw] lg:motion-safe:px-[4vw]"
      >
        {/* 01 — Cover */}
        <section className={`${chapterBase} pt-28 shell lg:motion-safe:pt-10 lg:motion-safe:w-[88vw] lg:motion-safe:max-w-none lg:motion-safe:px-0 grid grid-cols-1 lg:motion-safe:grid-cols-12 gap-8`}>
          <div className="lg:motion-safe:col-span-5 flex flex-col justify-between min-w-0">
            <div>
              <Link to="/projects" className={`${label} hover:text-foreground-950 cursor-pointer whitespace-nowrap`}>
                <i className="ri-arrow-left-line mr-2" />Projects
              </Link>
              <span className="block mt-8 text-[11px] text-accent-600 tracking-[0.2em]">{p.number} / {p.finish}</span>
              <h1 className="display mt-4 text-[clamp(2.4rem,5.5vw,5.5rem)]">{p.title}</h1>
              <p className="mt-6 max-w-md text-[15px] leading-relaxed text-foreground-700">{p.summary}</p>
            </div>
            <dl className="mt-10 border-t border-foreground-950">
              {facts.map((f) => (
                <div key={f.k} className="py-3 border-b border-background-300 grid grid-cols-3 gap-4">
                  <dt className={label}>{f.k}</dt>
                  <dd className="col-span-2 text-sm">{f.v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <Img src={p.cover} alt={`${p.title} exterior — ${p.finish}`} className={`lg:motion-safe:col-span-7 w-full aspect-[4/3] lg:motion-safe:aspect-auto ${H}`} eager priority="high" />
        </section>

        {/* 02 — The work */}
        <section className={`${chapterBase} shell lg:motion-safe:w-[84vw] lg:motion-safe:max-w-none lg:motion-safe:px-0 grid grid-cols-1 lg:motion-safe:grid-cols-12 gap-8`}>
          <div className="lg:motion-safe:col-span-4 flex flex-col justify-end min-w-0">
            <span className={label}>02 — The work</span>
            <div className="mt-6 flex flex-col gap-5 text-[15px] leading-relaxed text-foreground-700">
              {p.narrative.map((t) => <p key={t.slice(0, 20)}>{t}</p>)}
            </div>
          </div>
          {g2 && <Img src={g2} alt={`${p.title} elevation — ${p.surface}`} className={`lg:motion-safe:col-span-8 w-full aspect-[16/10] lg:motion-safe:aspect-auto ${H}`} eager priority="low" />}
        </section>

        {/* 03 — Architecture & surface: aligned pair */}
        {g0 && g1 && (
          <section className={`${chapterBase} shell lg:motion-safe:w-[78vw] lg:motion-safe:max-w-none lg:motion-safe:px-0 flex flex-col`}>
            <span className={label}>03 — Architecture &amp; surface</span>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6 lg:motion-safe:flex-1 lg:motion-safe:min-h-0">
              <Img src={g0} alt={`${p.title} wall detail 1`} className={`w-full aspect-[4/5] lg:motion-safe:aspect-auto ${H}`} eager priority="low" />
              <Img src={g1} alt={`${p.title} wall detail 2`} className={`w-full aspect-[4/5] lg:motion-safe:aspect-auto ${H}`} eager priority="low" />
            </div>
          </section>
        )}

        {/* 04 — Material record (text-only: no unused photographs remain, none duplicated) */}
        <section className={`${chapterBase} shell lg:motion-safe:w-[40vw] lg:motion-safe:max-w-none lg:motion-safe:px-0 flex flex-col justify-end`}>
          <span className={label}>04 — Material record</span>
          <p className="mt-6 font-heading font-bold uppercase tracking-tight text-2xl md:text-4xl leading-none">{p.finish}</p>
          <dl className="mt-10 border-t border-foreground-950">
            {facts.map((f) => (
              <div key={f.k} className="py-4 border-b border-background-300">
                <dt className={label}>{f.k}</dt>
                <dd className="mt-1 text-sm text-foreground-800">{f.v}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* 05 — Project navigation */}
        <nav aria-label="Project navigation" className={`${chapterBase} shell lg:motion-safe:w-[46vw] lg:motion-safe:max-w-none lg:motion-safe:px-0 lg:motion-safe:pr-[4vw] flex flex-col justify-end`}>
          <span className={label}>05 — Continue</span>
          <Link to={`/projects/${prev.slug}`} className="group mt-6 py-6 border-t border-foreground-950 cursor-pointer block">
            <span className={label}><i className="ri-arrow-left-line mr-2" />Previous</span>
            <span className="block mt-2 font-heading font-bold uppercase tracking-tight text-xl md:text-3xl group-hover:text-accent-600 transition-colors">{prev.title}</span>
          </Link>
          <Link to={`/projects/${next.slug}`} className="group py-6 border-t border-background-300 cursor-pointer block">
            <span className={label}>Next<i className="ri-arrow-right-line ml-2" /></span>
            <span className="block mt-2 font-heading font-bold uppercase tracking-tight text-xl md:text-3xl group-hover:text-accent-600 transition-colors">{next.title}</span>
          </Link>
          <div className="pt-6 border-t border-background-300 flex flex-wrap gap-x-8 gap-y-3">
            <Link to="/projects" className={`${label} hover:text-foreground-950 cursor-pointer whitespace-nowrap`}>
              <i className="ri-layout-grid-line mr-2" />Back to projects
            </Link>
            <Link to="/contact" className="text-[11px] uppercase tracking-[0.2em] text-accent-600 hover:text-accent-700 cursor-pointer whitespace-nowrap">
              Request an estimate<i className="ri-arrow-right-up-line ml-2" />
            </Link>
          </div>
        </nav>
      </div>
    </div>
  );
}